<template>
  <div
    v-if="ready"
    :class="bem()"
  >
    <SupportBreadcrumbs
      :class="bem('breadcrumbs')"
      :breadcrumbs="breadcrumbs"
    />

    <div :class="bem('content')">
      <VisionHeading
        :class="bem('title')"
        tag="h2"
        size="h2"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="parseText(article.title)" />
      </VisionHeading>

      <p
        :class="bem('updated')"
      >
        Last updated
        {{ $dayjs(article.updated_at * 1000).fromNow() }}
      </p>

      <div :class="bem('article')">
        <!-- eslint-disable vue/no-v-html -->
        <div
          :class="bem('body')"
          v-html="article.body"
        />
      </div>
    </div>

    <SupportArticleList
      v-if="relatedArticlesCategory.articles.length"
      :class="bem('related')"
      :category="relatedArticlesCategory"
      :limit="5"
    />

    <SupportRating
      :class="bem('rating')"
      :article="article"
    />
  </div>
</template>

<script setup>
import { parseText } from '~/assets/js/helpers/text.js'

const { resolveLocalePath } = useUrlBuilder()
const { runWithContext } = useNuxtApp()
const bem = useBEM('SupportArticlePage')
const route = useRoute()

const props = defineProps({
  categories: { type: Array, required: true },
  articles: { type: Array, required: true }
})

const categoryId = route.params?.category?.split('-')?.[0]
const flatCategories = props.categories.flatMap((category) => [
  category,
  ...(category.categories || [])
])
const category = flatCategories.find(
  (category) => categoryId && category.id === categoryId
)

const articleId = route.params?.article?.split('-')?.[0]
const article = category.articles.find(
  (article) => articleId && article.id === articleId
)

let breadcrumbs, relatedArticlesCategory
const ready = ref(false)

if (!category || !article) {
  await navigateTo({
    path: resolveLocalePath('/support'),
    redirectCode: 302
  })
} else {
  definePageMeta({
    pageType: 'Support Article Page'
  })

  const { getTitle, getDescription } = useMetaHelper()
  runWithContext(() => {
    const title = getTitle(`${article.title} | Support`)
    useHead({
      title,
      ...getDescription(article.description || article.title),
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
      ]
    })

    useJsonld({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: article.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: article.body
          }
        }
      ]
    })
  })

  breadcrumbs = computed(() => {
    const breadcrumbs = []

    // Find all parent categories
    let parentCategoryId = article.parent_ids?.[0]
    while (parentCategoryId) {
      const parentCategory = flatCategories.find(
        (category) => category.id === `${parentCategoryId}`
      )
      breadcrumbs.unshift({
        label: parentCategory.label || parentCategory.name,
        target: resolveLocalePath(parentCategory.link)
      })

      parentCategoryId = parentCategory.parent_ids?.[0]
        || parentCategory.parent_id
    }

    // Add root support page
    breadcrumbs.unshift({
      label: 'Support',
      target: resolveLocalePath('/support')
    })

    return breadcrumbs
  })

  relatedArticlesCategory = computed(() => {
    return {
      name: 'Related Articles',
      articles: category.articles.filter((a) => a.id !== article.id)
    }
  })

  onMounted(async () => {
    const { $track } = useNuxtApp()
    await $track.support.viewArticle(article)
  })

  ready.value = true
}
</script>

<style lang="postcss">
.SupportArticlePage {
  @apply sm:global-wrapper flex flex-col gap-8;
  --screen-size: 800px;
}

.SupportArticlePage__content {
  @apply flex flex-col gap-4;
}

.SupportArticlePage__updated {
  @apply text-gray-600 text-sm;
}

.SupportArticlePage__body {
  p {
    min-height: 1.5rem;
  }
}
</style>